// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Handles alert dialogs only
// 2. Separation of Concerns - UI separated from business logic
// 3. Modular Architecture - Reusable across entire project
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

/**
 * Reusable Alert Dialog Component
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onClose - Called when dialog closes
 * @param {string} title - Dialog title
 * @param {string} message - Dialog message/content
 * @param {string} buttonText - Text for OK button (default: "OK")
 * @param {string} severity - Dialog severity: "success", "error", "warning", "info" (default: "info")
 */
const AlertDialog = ({
  open,
  onClose,
  title = 'Alert',
  message,
  buttonText = 'OK',
  severity = 'info',
}) => {
  const getIcon = () => {
    switch (severity) {
      case 'success':
        return <SuccessIcon sx={{ fontSize: 48, color: 'success.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />;
      case 'warning':
        return <WarningIcon sx={{ fontSize: 48, color: 'warning.main' }} />;
      case 'info':
      default:
        return <InfoIcon sx={{ fontSize: 48, color: 'info.main' }} />;
    }
  };

  const getButtonColor = () => {
    switch (severity) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
      default:
        return 'primary';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          {getIcon()}
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ pl: 8 }}>
          {message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color={getButtonColor()} 
          autoFocus
          fullWidth
          sx={{ maxWidth: 120 }}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertDialog;
